import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { oddsManager, headToHeadManager } from '../services/api/managers';
import Header from '../components/Header';
import '../styles/index.css';
import HeadToHeadPage from './HeadToHeadPage';
import '../styles/components/H2HStats.css';
import '../styles/components/MatchDetailsPage.css';

const MatchDetailsPage = () => {
  const { fixtureId } = useParams();
  const location = useLocation();
  const { league, leagueId, time, teams } = location.state || {};
  
  const [odds, setOdds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestStatus, setRequestStatus] = useState('');
  const [statsResponse, setStatsResponse] = useState(null);
  const [selectedBookmakerIndex, setSelectedBookmakerIndex] = useState(0);
  const [availableBookmakers, setAvailableBookmakers] = useState([]);
  const h2hRef = useRef(null);
  const [h2hMatches, setH2hMatches] = useState([]);
  const [fixtureDetails, setFixtureDetails] = useState(null);
  const [oddsResponse, setOddsResponse] = useState(null);

  // Function to handle receiving H2H match data from the HeadToHeadPage component
  const handleH2hDataReceived = (matches) => {
    setH2hMatches(matches);
    if (matches && matches.length > 0) {
      fetchAverageStats(matches);
    }
  };

  // Function to fetch average statistics from match fixture IDs
  const fetchAverageStats = async (matches) => {
    try {
      setRequestStatus('Fetching average statistics...');
      
      // Extract fixture IDs from the match data (up to 5 most recent matches)
      const fixtureIDs = matches.slice(0, 5).map(match => match.fixture.id);
      
      console.log('Fetching average statistics for fixtures:', fixtureIDs);
      
      // Call the new method to get average stats
      const avgStatsResponse = await headToHeadManager.fetchAverageStatistics(fixtureIDs);
      
      if (avgStatsResponse.status === 'success' && avgStatsResponse.data) {
        setStatsResponse(avgStatsResponse);
        setRequestStatus('Average statistics fetched successfully');
        console.log('Average statistics:', avgStatsResponse);
      } else {
        console.error('Error fetching average statistics:', avgStatsResponse.message);
        // Fall back to single fixture stats if average stats fail
        const singleStatsResponse = await headToHeadManager.fetchStatisticsH2H(fixtureId);
        setStatsResponse(singleStatsResponse);
      }
    } catch (err) {
      console.error('Error fetching average statistics:', err);
      // Fall back to single fixture stats if average stats fail
      try {
        const singleStatsResponse = await headToHeadManager.fetchStatisticsH2H(fixtureId);
        setStatsResponse(singleStatsResponse);
      } catch (fallbackErr) {
        console.error('Error fetching fallback statistics:', fallbackErr);
      }
    }
  };

  // Function to fetch teams' recent match statistics
  const fetchTeamsRecentStats = async () => {
    try {
      // Check if we have both team IDs
      if (!teams?.home?.id || !teams?.away?.id) {
        console.error('Team IDs missing, cannot fetch team stats');
        return;
      }
      
      setRequestStatus('Fetching team recent statistics...');
      console.log(`Fetching recent stats for teams: ${teams.home.id} vs ${teams.away.id}`);
      
      // Call the method from headToHeadManager to fetch team stats
      const teamStatsResponse = await headToHeadManager.fetchTeamsRecentStats(
        teams.home.id, 
        teams.away.id
      );
      
      if (teamStatsResponse.status === 'success' && teamStatsResponse.data) {
        setStatsResponse(teamStatsResponse);
        setRequestStatus('Team statistics fetched successfully');
        console.log('Team statistics:', teamStatsResponse);
      } else {
        console.error('Error fetching team statistics:', teamStatsResponse.message);
        // Try to fall back to average stats from H2H matches
        if (h2hMatches && h2hMatches.length > 0) {
          await fetchAverageStats(h2hMatches);
        } else {
          // Last resort - fetch single fixture stats
          const singleStatsResponse = await headToHeadManager.fetchStatisticsH2H(fixtureId);
          setStatsResponse(singleStatsResponse);
        }
      }
    } catch (err) {
      console.error('Error fetching team statistics:', err);
      // Try to fall back to H2H average stats
      if (h2hMatches && h2hMatches.length > 0) {
        await fetchAverageStats(h2hMatches);
      } else {
        // Last resort - fetch single fixture stats
        try {
          const singleStatsResponse = await headToHeadManager.fetchStatisticsH2H(fixtureId);
          setStatsResponse(singleStatsResponse);
        } catch (fallbackErr) {
          console.error('Error fetching fallback statistics:', fallbackErr);
        }
      }
    }
  };

  useEffect(() => {
    if (fixtureId) {
      const fetchData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          if (teams && teams.home && teams.away) {
            fetchTeamsRecentStats();
          } else if (h2hMatches && h2hMatches.length > 0) {
            fetchAverageStats(h2hMatches);
          } else {
            // Fallback to single match stats
            const singleStatsResponse = await headToHeadManager.fetchStatisticsH2H(fixtureId);
            setStatsResponse(singleStatsResponse);
          }
          
          // Fetch odds data
          if (leagueId) {
            try {
              const oddsResponse = await oddsManager.fetchOddsByLeague(leagueId, 2024, fixtureId);
              
              if (oddsResponse && oddsResponse.status === 'success' && oddsResponse.data) {
                console.log('Number of odds responses:', oddsResponse.data.response.length);
                if (oddsResponse.data.response.length > 0) {
                  console.log('First odds bookmakers:', oddsResponse.data.response[0].bookmakers ? oddsResponse.data.response[0].bookmakers.length : 'none');
                  
                  // Extract the first 5 bookmakers
                  if (oddsResponse.data.response[0]?.bookmakers && 
                      Array.isArray(oddsResponse.data.response[0].bookmakers)) {
                    const firstFiveBookmakers = oddsResponse.data.response[0].bookmakers.slice(0, 5);
                    setAvailableBookmakers(firstFiveBookmakers);
                  }
                }
                setOdds(oddsResponse.data.response);
                setOddsResponse(oddsResponse);
              } else {
                setError('No odds data available');
              }
            } catch (oddsError) {
              console.error('Error fetching odds:', oddsError);
              setError('Error fetching odds data: ' + (oddsError.message || 'Unknown error'));
            }
          }
          
          setLoading(false);
        } catch (error) {
          console.error('Error in useEffect:', error);
          setError('Error loading match data: ' + (error.message || 'Unknown error'));
          setLoading(false);
        }
      };

      fetchData();
    }
  // Add teams and h2hMatches to the dependency array so we can refetch when they change
  }, [fixtureId, teams, h2hMatches, leagueId]);

  const handleBookmakerChange = (event) => {
    setSelectedBookmakerIndex(parseInt(event.target.value, 10));
  };

  return (
    <div className="match-details-page">
      <Header title="Match Details" />
      {/* <div className="match-header">
        <h2></h2>
        <div className="match-info">
          {league && <p><strong>League:</strong> {league} {time && <span>• <strong>Time:</strong> {time}</span>}</p>}
          {leagueId && <p><strong>League ID:</strong> {leagueId}</p>}
          <p><strong>Fixture ID:</strong> {fixtureId}</p>
          {teams && (
            <>
              <p><strong>Teams:</strong> {teams.home.name} vs {teams.away.name}</p>
              <p><strong>Home Team ID:</strong> {teams.home.id || 'N/A'}</p>
              <p><strong>Away Team ID:</strong> {teams.away.id || 'N/A'}</p>
            </>
          )}
        </div>
      </div> */}

      <div className="match-content">
        {loading ? (
          <div className="loading">Loading odds data...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <div className="visual-odds-container">
            {/* Team Match-up Visualization */}
            <div className="match-visualization" style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f0f3f5', borderRadius: '10px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '20px' }}>Match Overview</h3>
              <div className="teams-container" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                maxWidth: '100%',
                margin: '0 auto'
              }}>
                <div className="home-team" style={{ 
                  textAlign: 'center', 
                  flex: '1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '0 10px'
                }}>
                  <div className="team-logo" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100px'
                  }}>
                  {teams && (
                    <img src={teams.home.logo} alt={teams.home.name} style={{ width: '80px', height: '80px' }} />
                  )}
                  </div>
                  <div className="team-name" style={{ 
                    marginTop: '10px', 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#34495e',
                    wordWrap: 'break-word',
                    width: '100%',
                    textAlign: 'center',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: '1.3'
                  }}>
                    {teams && teams.home.name && teams.home.name.split(' ').map((word, index) => (
                      <span key={index} style={{display: 'block', width: '100%'}}>
                        {word}{index < teams.home.name.split(' ').length - 1 ? ' ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="versus" style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: '#7f8c8d',
                  margin: '0 15px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0 10px'
                }}>VS</div>
                
                <div className="away-team" style={{ 
                  textAlign: 'center', 
                  flex: '1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '0 10px'
                }}>
                  <div className="team-logo" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100px'
                  }}>
                  {teams && (
                    <img src={teams.away.logo} alt={teams.away.name} style={{ width: '80px', height: '80px' }} />
                  )}
                  </div>
                  <div className="team-name" style={{ 
                    marginTop: '10px', 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#34495e',
                    wordWrap: 'break-word',
                    width: '100%',
                    textAlign: 'center',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    lineHeight: '1.3'
                  }}>
                    {teams && teams.away.name && teams.away.name.split(' ').map((word, index) => (
                      <span key={index} style={{display: 'block', width: '100%'}}>
                        {word}{index < teams.away.name.split(' ').length - 1 ? ' ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bookmakers Section */}
            <div className="bookmakers-section" style={{ marginTop: '20px' }}>
              <h3 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '20px' }}>Bookmakers</h3>
              
              {availableBookmakers.length > 0 ? (
                <div className="bookmaker-selector" style={{ marginBottom: '20px', textAlign: 'center' }}>
                  <label htmlFor="bookmaker-select" style={{ 
                    marginRight: '10px', 
                    fontWeight: 'bold', 
                    fontSize: '16px',
                    color: '#2c3e50'
                  }}>Select Bookmaker: </label>

                  
                  <select 
                    id="bookmaker-select" 
                    value={selectedBookmakerIndex} 
                    onChange={handleBookmakerChange}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '5px',
                      border: '1px solid #ddd',
                      backgroundColor: '#fff',
                      fontSize: '16px',
                      color: '#333',
                      cursor: 'pointer',
                      width: 'auto',
                      minWidth: '200px'
                    }}
                  >
                    {availableBookmakers.map((bookmaker, index) => (
                      <option key={index} value={index}>
                        {bookmaker.name || `Bookmaker ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              
              {availableBookmakers.length > 0 ? (
                <div className="bookmaker-details-container">
                  <div className="bookmaker-card" style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '10px', 
                    padding: '20px', 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                    backgroundColor: '#ffffff',
                    maxWidth: '800px',
                    margin: '0 auto'
                  }}>
                    <div className="bookmaker-header" style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      borderBottom: '2px solid #f39c12', 
                      paddingBottom: '10px', 
                      marginBottom: '10px' 
                    }}>
                      <h4 style={{ color: '#333', margin: '0', fontSize: '18px' }}>
                        {availableBookmakers[selectedBookmakerIndex].name || 'Unknown Bookmaker'}
                      </h4>
                    </div>
                    
                    {/* Bookmaker Details */}
                    <div className="bookmaker-details" style={{ 
                      backgroundColor: '#f9f9f9', 
                      margin: '10px 0', 
                      borderRadius: '5px' 
                    }}>
                    </div>
                    
                    {/* Bets Array Display */}
                    <div className="bets-array" style={{ margin: '15px 0' }}>
                      <h5 style={{ 
                        backgroundColor: '#2c3e50', 
                        color: 'white', 
                        padding: '12px 15px', 
                        borderRadius: '5px 5px 0 0', 
                        margin: '0',
                        fontSize: '16px'
                      }}>Available Bets</h5>
                      
                      {Array.isArray(availableBookmakers[selectedBookmakerIndex].bets) && 
                       availableBookmakers[selectedBookmakerIndex].bets.length > 0 ? (
                        <div className="bets-list" style={{ 
                          border: '1px solid #ddd', 
                          borderTop: 'none', 
                          borderRadius: '0 0 5px 5px', 
                          overflow: 'hidden' 
                        }}>
                          {availableBookmakers[selectedBookmakerIndex].bets.slice(0, 3).map((bet, betIndex) => (
                            <div key={betIndex} className="bet-item" style={{ 
                              padding: '15px', 
                              borderBottom: betIndex < Math.min(availableBookmakers[selectedBookmakerIndex].bets.slice(0, 3).length - 1, 2) ? '1px solid #eee' : 'none', 
                              backgroundColor: betIndex % 2 === 0 ? '#fff' : '#f9f9f9' 
                            }}>
                              <div className="bet-header" style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                marginBottom: '10px' 
                              }}>
                                <h6 style={{ 
                                  margin: '0', 
                                  color: '#2c3e50', 
                                  fontSize: '16px',
                                  fontWeight: 'bold'
                                }}>{bet.name || 'Unknown Bet'}</h6>
                              </div>
                              
                              {Array.isArray(bet.values) && bet.values.length > 0 ? (
                                <div className="values-table" style={{ marginTop: '10px' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr>
                                        <th style={{ 
                                          textAlign: 'left', 
                                          padding: '10px', 
                                          backgroundColor: '#ecf0f1', 
                                          borderBottom: '1px solid #ddd' 
                                        }}>Value</th>
                                        <th style={{ 
                                          textAlign: 'right', 
                                          padding: '10px', 
                                          backgroundColor: '#ecf0f1', 
                                          borderBottom: '1px solid #ddd' 
                                        }}>Odd</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {bet.values.map((value, valueIndex) => (
                                        <tr key={valueIndex}>
                                          <td style={{ 
                                            padding: '10px', 
                                            borderBottom: '1px solid #eee' 
                                          }}>{value.value || 'Unknown'}</td>
                                          <td style={{ 
                                            textAlign: 'right', 
                                            padding: '10px', 
                                            borderBottom: '1px solid #eee', 
                                            fontWeight: 'bold', 
                                            color: '#2980b9' 
                                          }}>{value.odd || 'N/A'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p style={{ 
                                  color: '#777', 
                                  fontStyle: 'italic', 
                                  margin: '5px 0' 
                                }}>No values available for this bet</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ 
                          color: '#777', 
                          fontStyle: 'italic', 
                          padding: '15px', 
                          borderBottom: '1px solid #ddd', 
                          borderLeft: '1px solid #ddd', 
                          borderRight: '1px solid #ddd', 
                          borderRadius: '0 0 5px 5px', 
                          margin: '0' 
                        }}>No bets available from this bookmaker</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-data" style={{ textAlign: 'center', padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ color: '#777', fontSize: '16px' }}>No odds data available for this match</p>
                  <div style={{ marginTop: '15px', width: '100px', height: '100px', backgroundColor: '#eee', borderRadius: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <span style={{ fontSize: '24px', color: '#999' }}>?</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Render HeadToHeadPage at the end */}
      <HeadToHeadPage 
        leagueId={leagueId} 
        homeTeamId={teams.home.id} 
        awayTeamId={teams.away.id} 
        homeTeamLogo={teams.home.logo}
        awayTeamLogo={teams.away.logo}
        homeTeamName={teams.home.name}
        awayTeamName={teams.away.name}
        onH2hDataReceived={handleH2hDataReceived}
      />

      
      {statsResponse && statsResponse.data && statsResponse.data.response && statsResponse.data.response.length > 0 && (
  <div className="stats-visualization">
    <h3>Head to Head Statistics</h3>
    {statsResponse.data.fixturesCount ? (
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '15px', 
        color: '#3498db', 
        fontWeight: 'bold',
        fontSize: '14px',
        padding: '8px',
        backgroundColor: 'rgba(52, 152, 219, 0.1)',
        borderRadius: '5px',
        fontFamily: 'Arial, sans-serif'
      }}>
        Showing average statistics from {statsResponse.data.fixturesCount} recent matches
      </div>
    ) : null}
    <div className="teams-comparison-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
      <div style={{ textAlign: 'left', flex: 1, display: 'flex', alignItems: 'center' }}>
        <img src={statsResponse.data.response[0].team.logo} alt="Home team" 
          style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
        <div style={{ 
          marginLeft: '10px', 
          fontWeight: 'bold',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          wordWrap: 'break-word',
          minHeight: '40px',
          justifyContent: 'center',
          lineHeight: '1.3'
        }}>
          {statsResponse.data.response[0].team.name.split(' ').map((word, index) => (
            <span key={index} style={{display: 'block', width: '100%'}}>
              {word}{index < statsResponse.data.response[0].team.name.split(' ').length - 1 ? ' ' : ''}
            </span>
          ))}
        </div>
      </div>
      <div style={{ textAlign: 'right', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <div style={{ 
          marginRight: '10px', 
          fontWeight: 'bold',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          wordWrap: 'break-word',
          minHeight: '40px',
          justifyContent: 'center',
          alignItems: 'flex-end',
          lineHeight: '1.3'
        }}>
          {statsResponse.data.response[1].team.name.split(' ').map((word, index) => (
            <span key={index} style={{display: 'block', width: '100%', textAlign: 'right'}}>
              {word}{index < statsResponse.data.response[1].team.name.split(' ').length - 1 ? ' ' : ''}
            </span>
          ))}
        </div>
        <img src={statsResponse.data.response[1].team.logo} alt="Away team" 
          style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
      </div>
    </div>
    
    <div className="stats-comparison">
      {statsResponse.data.response[0].statistics.map((homeStat, index) => {
        const awayStat = statsResponse.data.response[1].statistics[index];
        
        // Форматирование типа статистики
        const formatStatType = (type) => {
          if (!type) return '';
          
          // Заменяем _ на пробелы
          const withSpaces = type.replace(/_/g, ' ');
          
          // Делаем первую букву каждого слова заглавной
          return withSpaces
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };
        
        const formattedStatType = formatStatType(homeStat.type);
        
        // Проверяем на null и пустые значения
        const isHomeValueNull = homeStat.value === null || homeStat.value === 'null' || homeStat.value === '';
        const isAwayValueNull = awayStat.value === null || awayStat.value === 'null' || awayStat.value === '';
        
        // Попытка преобразовать значения в числа для сравнения
        const homeValue = isHomeValueNull ? null : (parseFloat(homeStat.value) || 0);
        const awayValue = isAwayValueNull ? null : (parseFloat(awayStat.value) || 0);
        
        // Рассчитываем процентное соотношение только если оба значения не null
        const total = (homeValue !== null && awayValue !== null) ? (homeValue + awayValue) : 0;
        const homePercent = (homeValue !== null && awayValue !== null && total > 0) ? (homeValue / total) * 100 : 50;
        const awayPercent = (homeValue !== null && awayValue !== null && total > 0) ? (awayValue / total) * 100 : 50;
        
        return (
          <div key={index} className="stat-comparison-item" style={{ marginBottom: '15px' }}>
            {(!isHomeValueNull || !isAwayValueNull) && (
              <div className="stat-type" style={{ textAlign: 'center', marginBottom: '5px', fontWeight: 'bold' }}>
                {formattedStatType}
              </div>
            )}
            
            <div className="stat-values" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <div className="home-value" style={{ flex: '1', textAlign: 'left' }}>{homeStat.value}</div>
              <div className="away-value" style={{ flex: '1', textAlign: 'right' }}>{awayStat.value}</div>
            </div>
            
            {!isHomeValueNull && !isAwayValueNull && (
              <div className="stat-bar" style={{ display: 'flex', height: '20px', borderRadius: '10px', overflow: 'hidden' }}>
                <div className="home-bar" style={{ 
                  width: `${homePercent}%`, 
                  backgroundColor: '#3498db', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {homePercent > 15 && `${Math.round(homePercent)}%`}
                </div>
                <div className="away-bar" style={{ 
                  width: `${awayPercent}%`, 
                  backgroundColor: '#e74c3c',
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {awayPercent > 15 && `${Math.round(awayPercent)}%`}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}
    </div>
  );
};

export default MatchDetailsPage; 